import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ChevronRight,
  Cog,
  DoorOpen,
  Frame,
  Hammer,
  Home,
  PencilRuler,
  Plus,
  Printer,
  Receipt,
  RotateCcw,
  Ruler,
  Scissors,
  Shovel,
  ShoppingBag,
  Sparkles,
  Trash2,
  TreePine,
  TrendingDown,
  TrendingUp,
  Wrench,
} from 'lucide-react';
import './ShedCostCalculator.css';

const STORAGE_KEY = 'shedCostCalculator:v1';

type Id = string;

type Worker = {
  id: Id;
  label: string;
  hoursPerDay: number;
  ratePerHour: number;
  isMe: boolean;
};

type DoorWindow = {
  id: Id;
  label: string;
  qty: number;
  costEach: number;
};

type State = {
  buy: {
    footprintSqft: number;
    purchasePrice: number;
    deliveryFee: number;
  };
  build: {
    footprintSqft: number;
  };
  timberHarvest: {
    days: number;
    workers: Worker[];
    skidsteerGalPerDay: number;
    skidsteerDays: number;
    dieselPricePerGal: number;
    chainsawBarOil: number;
    chainsawGas: number;
  };
  sawmill: {
    hours: number;
    galPerHour: number;
    dieselPricePerGal: number;
    bladeReplacements: number;
    bladePrice: number;
    operatorHours: number;
    operatorRate: number;
    operatorIsMe: boolean;
  };
  design: {
    onshapeHours: number;
    onshapeRate: number;
    pages: number;
    pricePerPage: number;
    onshapeIsMe: boolean;
  };
  sitePrep: {
    augerRentalDays: number;
    augerDailyRate: number;
    holes: number;
    sonotubes: number;
    sonotubePrice: number;
    postBases: number;
    postBasePrice: number;
    concreteYards: number;
    concretePricePerYard: number;
    concreteDelivery: number;
    gravelTrucks: number;
    gravelPricePerTruck: number;
    skidsteerGal: number;
    dieselPricePerGal: number;
  };
  frameCutting: {
    guys: number;
    days: number;
    hoursPerDay: number;
    ratePerHour: number;
    piecesPerDay: number;
    includeMe: boolean;
  };
  raising: {
    days: number;
    skidsteerGalPerDay: number;
    tractorGalPerDay: number;
    dieselPricePerGal: number;
    crew: Worker[];
  };
  roof: {
    materialCost: number;
    accessoriesCost: number;
    laborHours: number;
    laborRate: number;
    laborIsMe: boolean;
  };
  hardware: {
    fastenersCost: number;
    finishCost: number;
  };
  doorsWindows: DoorWindow[];
  trim: {
    lumpSum: number;
  };
  taxContingency: {
    salesTaxPct: number;
    contingencyPct: number;
  };
  options: {
    opportunityCostEnabled: boolean;
    opportunityCostRate: number;
    extraPersonalHours: number;
    equipmentWearEnabled: boolean;
    sawmillWearHours: number;
    sawmillWearRate: number;
    skidsteerWearHours: number;
    skidsteerWearRate: number;
    tractorWearHours: number;
    tractorWearRate: number;
  };
};

const newId = (): Id =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const DEFAULT_STATE: State = {
  buy: {
    footprintSqft: 1200,
    purchasePrice: 45000,
    deliveryFee: 1500,
  },
  build: {
    footprintSqft: 1200,
  },
  timberHarvest: {
    days: 5,
    workers: [
      { id: newId(), label: 'Me', hoursPerDay: 8, ratePerHour: 35, isMe: true },
      { id: newId(), label: 'Brother', hoursPerDay: 8, ratePerHour: 25, isMe: false },
    ],
    skidsteerGalPerDay: 8,
    skidsteerDays: 5,
    dieselPricePerGal: 3.85,
    chainsawBarOil: 30,
    chainsawGas: 80,
  },
  sawmill: {
    hours: 40,
    galPerHour: 0.6,
    dieselPricePerGal: 3.85,
    bladeReplacements: 4,
    bladePrice: 35,
    operatorHours: 40,
    operatorRate: 35,
    operatorIsMe: true,
  },
  design: {
    onshapeHours: 10,
    onshapeRate: 50,
    pages: 100,
    pricePerPage: 0.1,
    onshapeIsMe: true,
  },
  sitePrep: {
    augerRentalDays: 1,
    augerDailyRate: 150,
    holes: 12,
    sonotubes: 12,
    sonotubePrice: 18,
    postBases: 12,
    postBasePrice: 35,
    concreteYards: 3,
    concretePricePerYard: 175,
    concreteDelivery: 100,
    gravelTrucks: 3,
    gravelPricePerTruck: 350,
    skidsteerGal: 10,
    dieselPricePerGal: 3.85,
  },
  frameCutting: {
    guys: 2,
    days: 6,
    hoursPerDay: 8,
    ratePerHour: 30,
    piecesPerDay: 6,
    includeMe: true,
  },
  raising: {
    days: 3,
    skidsteerGalPerDay: 8,
    tractorGalPerDay: 6,
    dieselPricePerGal: 3.85,
    crew: [
      { id: newId(), label: 'Me', hoursPerDay: 10, ratePerHour: 35, isMe: true },
      { id: newId(), label: 'Brother', hoursPerDay: 10, ratePerHour: 25, isMe: false },
      { id: newId(), label: 'Helper', hoursPerDay: 10, ratePerHour: 20, isMe: false },
    ],
  },
  roof: {
    materialCost: 4500,
    accessoriesCost: 800,
    laborHours: 24,
    laborRate: 35,
    laborIsMe: false,
  },
  hardware: {
    fastenersCost: 600,
    finishCost: 800,
  },
  doorsWindows: [
    { id: newId(), label: 'Sliding equipment door', qty: 1, costEach: 1800 },
    { id: newId(), label: 'Walk door', qty: 1, costEach: 350 },
  ],
  trim: { lumpSum: 600 },
  taxContingency: {
    salesTaxPct: 9.25,
    contingencyPct: 10,
  },
  options: {
    opportunityCostEnabled: false,
    opportunityCostRate: 75,
    extraPersonalHours: 0,
    equipmentWearEnabled: false,
    sawmillWearHours: 40,
    sawmillWearRate: 8,
    skidsteerWearHours: 80,
    skidsteerWearRate: 12,
    tractorWearHours: 30,
    tractorWearRate: 10,
  },
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

// Merge persisted state on top of defaults so newly-added fields don't break.
function mergeDeep<T>(base: T, override: unknown): T {
  if (override === null || override === undefined) return base;
  if (Array.isArray(base)) {
    return (Array.isArray(override) ? override : base) as T;
  }
  if (!isPlainObject(base)) {
    return (typeof override === typeof base ? (override as T) : base);
  }
  const result: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  if (isPlainObject(override)) {
    for (const key of Object.keys(base as Record<string, unknown>)) {
      if (Object.prototype.hasOwnProperty.call(override, key)) {
        result[key] = mergeDeep((base as Record<string, unknown>)[key], override[key]);
      }
    }
  }
  return result as T;
}

function loadState(): State {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return DEFAULT_STATE;
    return mergeDeep(DEFAULT_STATE, JSON.parse(raw));
  } catch {
    return DEFAULT_STATE;
  }
}

const fmtMoney = (n: number): string =>
  (n < 0 ? '-' : '') +
  '$' +
  Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtMoneyCents = (n: number): string =>
  (n < 0 ? '-' : '') +
  '$' +
  Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtPct = (n: number): string =>
  (n >= 0 ? '+' : '') + n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%';

const fmtNum = (n: number, digits = 0): string =>
  n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

// Numeric field that keeps its own text buffer so users can type "1." or "" without snapping back to 0.
type NumFieldProps = {
  label?: React.ReactNode;
  value: number;
  onChange: (n: number) => void;
  prefix?: string;
  suffix?: string;
  min?: number;
  step?: number;
  className?: string;
  compact?: boolean;
};

const NumField: React.FC<NumFieldProps> = ({
  label,
  value,
  onChange,
  prefix,
  suffix,
  min = 0,
  step,
  className,
  compact,
}) => {
  const [text, setText] = useState<string>(() =>
    Number.isFinite(value) ? (value === 0 ? '' : String(value)) : ''
  );
  const lastReportedRef = useRef<number>(value);

  useEffect(() => {
    if (value !== lastReportedRef.current) {
      setText(Number.isFinite(value) ? (value === 0 ? '' : String(value)) : '');
      lastReportedRef.current = value;
    }
  }, [value]);

  return (
    <label className={`scc-field${compact ? ' scc-field--compact' : ''}${className ? ' ' + className : ''}`}>
      {label !== undefined && <span className="scc-field-label">{label}</span>}
      <span className="scc-field-input">
        {prefix && <span className="scc-affix scc-affix--prefix">{prefix}</span>}
        <input
          type="number"
          inputMode="decimal"
          min={min}
          step={step}
          value={text}
          onChange={(e) => {
            const t = e.target.value;
            setText(t);
            const parsed = parseFloat(t);
            const n = Number.isFinite(parsed) ? parsed : 0;
            lastReportedRef.current = n;
            onChange(n);
          }}
          onBlur={() => {
            // Normalize display once the user leaves the field.
            const parsed = parseFloat(text);
            const n = Number.isFinite(parsed) ? parsed : 0;
            setText(n === 0 ? '' : String(n));
          }}
        />
        {suffix && <span className="scc-affix scc-affix--suffix">{suffix}</span>}
      </span>
    </label>
  );
};

type SectionProps = {
  icon: React.ReactNode;
  title: string;
  subtotal: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
};

const Section: React.FC<SectionProps> = ({ icon, title, subtotal, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`scc-section${open ? ' scc-section--open' : ''}`}>
      <button
        type="button"
        className="scc-section-header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="scc-section-icon">{icon}</span>
        <span className="scc-section-title">{title}</span>
        <span className="scc-section-subtotal">{fmtMoney(subtotal)}</span>
        <ChevronRight className={`scc-chevron${open ? ' scc-chevron--open' : ''}`} size={18} />
      </button>
      {open && <div className="scc-section-body">{children}</div>}
    </section>
  );
};

const ShedCostCalculator: React.FC = () => {
  const [state, setState] = useState<State>(() => loadState());

  // Debounced persistence to localStorage.
  useEffect(() => {
    const t = window.setTimeout(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch {
        // ignore quota / private mode errors
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [state]);

  // Tab title isolation since the page is intentionally hidden.
  useEffect(() => {
    const previous = document.title;
    document.title = 'Shed Cost Calculator';
    return () => {
      document.title = previous;
    };
  }, []);

  const update = <K extends keyof State>(key: K, patch: Partial<State[K]>) =>
    setState((s) => ({ ...s, [key]: { ...(s[key] as object), ...patch } } as State));

  const updateWorkerList = (
    section: 'timberHarvest' | 'raising',
    listKey: 'workers' | 'crew',
    next: Worker[]
  ) =>
    setState((s) => ({
      ...s,
      [section]: { ...(s[section] as object), [listKey]: next },
    } as State));

  const updateDoorsWindows = (next: DoorWindow[]) =>
    setState((s) => ({ ...s, doorsWindows: next }));

  const handleReset = () => {
    if (!window.confirm('Reset all inputs to the seeded defaults? This will clear your saved values.')) return;
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    // Use freshly-seeded ids so React re-mounts list rows cleanly.
    setState({
      ...DEFAULT_STATE,
      timberHarvest: {
        ...DEFAULT_STATE.timberHarvest,
        workers: DEFAULT_STATE.timberHarvest.workers.map((w) => ({ ...w, id: newId() })),
      },
      raising: {
        ...DEFAULT_STATE.raising,
        crew: DEFAULT_STATE.raising.crew.map((w) => ({ ...w, id: newId() })),
      },
      doorsWindows: DEFAULT_STATE.doorsWindows.map((d) => ({ ...d, id: newId() })),
    });
  };

  const handlePrint = () => window.print();

  const calc = useMemo(() => {
    const buyTotalAll = state.buy.purchasePrice + state.buy.deliveryFee;
    const buyPerSqft = state.buy.footprintSqft > 0 ? buyTotalAll / state.buy.footprintSqft : 0;

    const thLabor = state.timberHarvest.workers.reduce(
      (sum, w) => sum + w.hoursPerDay * w.ratePerHour * state.timberHarvest.days,
      0
    );
    const thFuel =
      state.timberHarvest.skidsteerGalPerDay *
      state.timberHarvest.skidsteerDays *
      state.timberHarvest.dieselPricePerGal;
    const thConsumables = state.timberHarvest.chainsawBarOil + state.timberHarvest.chainsawGas;
    const timberHarvestTotal = thLabor + thFuel + thConsumables;

    const smFuel = state.sawmill.hours * state.sawmill.galPerHour * state.sawmill.dieselPricePerGal;
    const smBlades = state.sawmill.bladeReplacements * state.sawmill.bladePrice;
    const smLabor = state.sawmill.operatorHours * state.sawmill.operatorRate;
    const sawmillTotal = smFuel + smBlades + smLabor;

    const dsLabor = state.design.onshapeHours * state.design.onshapeRate;
    const dsPrint = state.design.pages * state.design.pricePerPage;
    const designTotal = dsLabor + dsPrint;

    const spRental = state.sitePrep.augerRentalDays * state.sitePrep.augerDailyRate;
    const spSono = state.sitePrep.sonotubes * state.sitePrep.sonotubePrice;
    const spBases = state.sitePrep.postBases * state.sitePrep.postBasePrice;
    const spConc =
      state.sitePrep.concreteYards * state.sitePrep.concretePricePerYard +
      state.sitePrep.concreteDelivery;
    const spGravel = state.sitePrep.gravelTrucks * state.sitePrep.gravelPricePerTruck;
    const spFuel = state.sitePrep.skidsteerGal * state.sitePrep.dieselPricePerGal;
    const sitePrepTotal = spRental + spSono + spBases + spConc + spGravel + spFuel;

    const fcLabor =
      state.frameCutting.guys *
      state.frameCutting.days *
      state.frameCutting.hoursPerDay *
      state.frameCutting.ratePerHour;
    const totalPieces = state.frameCutting.days * state.frameCutting.piecesPerDay;
    const frameCuttingTotal = fcLabor;

    const rsCrew = state.raising.crew.reduce(
      (sum, w) => sum + w.hoursPerDay * w.ratePerHour * state.raising.days,
      0
    );
    const rsSki =
      state.raising.skidsteerGalPerDay * state.raising.days * state.raising.dieselPricePerGal;
    const rsTrac =
      state.raising.tractorGalPerDay * state.raising.days * state.raising.dieselPricePerGal;
    const raisingTotal = rsCrew + rsSki + rsTrac;

    const roofLabor = state.roof.laborHours * state.roof.laborRate;
    const roofTotal = state.roof.materialCost + state.roof.accessoriesCost + roofLabor;

    const hardwareTotal = state.hardware.fastenersCost + state.hardware.finishCost;

    const doorsWindowsTotal = state.doorsWindows.reduce(
      (sum, d) => sum + d.qty * d.costEach,
      0
    );

    const trimTotal = state.trim.lumpSum;

    const subtotal =
      timberHarvestTotal +
      sawmillTotal +
      designTotal +
      sitePrepTotal +
      frameCuttingTotal +
      raisingTotal +
      roofTotal +
      hardwareTotal +
      doorsWindowsTotal +
      trimTotal;

    // Materials base — only the actually-purchased material portions get sales tax.
    const materialsBase =
      thConsumables +
      smBlades +
      dsPrint +
      spSono +
      spBases +
      spConc +
      spGravel +
      state.roof.materialCost +
      state.roof.accessoriesCost +
      hardwareTotal +
      doorsWindowsTotal +
      trimTotal;

    const tax = materialsBase * (state.taxContingency.salesTaxPct / 100);

    // ----- Self-labor: cost of work YOU personally do, by section -----
    // Used for two purposes:
    //   1. Opportunity-cost hours total
    //   2. The "if I don't pay myself" comparison total
    const myHarvestLabor = state.timberHarvest.workers
      .filter((w) => w.isMe)
      .reduce((sum, w) => sum + w.hoursPerDay * w.ratePerHour * state.timberHarvest.days, 0);
    const myRaisingLabor = state.raising.crew
      .filter((w) => w.isMe)
      .reduce((sum, w) => sum + w.hoursPerDay * w.ratePerHour * state.raising.days, 0);
    const mySawmillLabor = state.sawmill.operatorIsMe ? smLabor : 0;
    const myDesignLabor = state.design.onshapeIsMe ? dsLabor : 0;
    // Frame cutting: if you're one of the guys, your share is exactly 1 person's worth.
    const myFrameLabor = state.frameCutting.includeMe
      ? state.frameCutting.days * state.frameCutting.hoursPerDay * state.frameCutting.ratePerHour
      : 0;
    const myRoofLabor = state.roof.laborIsMe ? roofLabor : 0;
    const selfLaborTotal =
      myHarvestLabor +
      myRaisingLabor +
      mySawmillLabor +
      myDesignLabor +
      myFrameLabor +
      myRoofLabor;

    // Opportunity-cost hours mirror the same isMe flags.
    const myHoursFromHarvest = state.timberHarvest.workers
      .filter((w) => w.isMe)
      .reduce((sum, w) => sum + w.hoursPerDay * state.timberHarvest.days, 0);
    const myHoursFromRaising = state.raising.crew
      .filter((w) => w.isMe)
      .reduce((sum, w) => sum + w.hoursPerDay * state.raising.days, 0);
    const myHoursFromMill = state.sawmill.operatorIsMe ? state.sawmill.operatorHours : 0;
    const myHoursFromDesign = state.design.onshapeIsMe ? state.design.onshapeHours : 0;
    const myHoursFromFrame = state.frameCutting.includeMe
      ? state.frameCutting.days * state.frameCutting.hoursPerDay
      : 0;
    const myHoursFromRoof = state.roof.laborIsMe ? state.roof.laborHours : 0;
    const myHoursTotal =
      myHoursFromHarvest +
      myHoursFromRaising +
      myHoursFromMill +
      myHoursFromDesign +
      myHoursFromFrame +
      myHoursFromRoof +
      state.options.extraPersonalHours;
    const opportunityCost = state.options.opportunityCostEnabled
      ? myHoursTotal * state.options.opportunityCostRate
      : 0;

    const sawmillWear = state.options.sawmillWearHours * state.options.sawmillWearRate;
    const skidsteerWear = state.options.skidsteerWearHours * state.options.skidsteerWearRate;
    const tractorWear = state.options.tractorWearHours * state.options.tractorWearRate;
    const equipmentWear = state.options.equipmentWearEnabled
      ? sawmillWear + skidsteerWear + tractorWear
      : 0;

    const contingencyPct = state.taxContingency.contingencyPct / 100;

    // "If I pay myself" — full labor at entered rates.
    const beforeContingency = subtotal + tax + opportunityCost + equipmentWear;
    const contingency = beforeContingency * contingencyPct;
    const buildTotal = beforeContingency + contingency;
    const buildPerSqft = state.build.footprintSqft > 0 ? buildTotal / state.build.footprintSqft : 0;
    const dollarDiff = buildTotal - buyTotalAll;
    const pctDiffOnTotal = buyTotalAll > 0 ? (dollarDiff / buyTotalAll) * 100 : 0;
    const sqftDollarDiff = buildPerSqft - buyPerSqft;
    const pctDiffOnSqft = buyPerSqft > 0 ? (sqftDollarDiff / buyPerSqft) * 100 : 0;

    // "If I don't pay myself" — strip out my labor cost; tax/wear/opportunity all unaffected.
    const subtotalUnpaid = Math.max(subtotal - selfLaborTotal, 0);
    const beforeContingencyUnpaid = subtotalUnpaid + tax + opportunityCost + equipmentWear;
    const contingencyUnpaid = beforeContingencyUnpaid * contingencyPct;
    const buildTotalUnpaid = beforeContingencyUnpaid + contingencyUnpaid;
    const buildPerSqftUnpaid =
      state.build.footprintSqft > 0 ? buildTotalUnpaid / state.build.footprintSqft : 0;
    const dollarDiffUnpaid = buildTotalUnpaid - buyTotalAll;
    const pctDiffOnTotalUnpaid =
      buyTotalAll > 0 ? (dollarDiffUnpaid / buyTotalAll) * 100 : 0;
    const sqftDollarDiffUnpaid = buildPerSqftUnpaid - buyPerSqft;
    const pctDiffOnSqftUnpaid =
      buyPerSqft > 0 ? (sqftDollarDiffUnpaid / buyPerSqft) * 100 : 0;

    const breakdown: Array<{ name: string; value: number; kind: 'section' | 'modifier' }> = [
      { name: 'Timber harvest', value: timberHarvestTotal, kind: 'section' },
      { name: 'Sawmill', value: sawmillTotal, kind: 'section' },
      { name: 'Design', value: designTotal, kind: 'section' },
      { name: 'Site prep', value: sitePrepTotal, kind: 'section' },
      { name: 'Frame cutting', value: frameCuttingTotal, kind: 'section' },
      { name: 'Raising', value: raisingTotal, kind: 'section' },
      { name: 'Roof', value: roofTotal, kind: 'section' },
      { name: 'Hardware & finish', value: hardwareTotal, kind: 'section' },
      { name: 'Doors & windows', value: doorsWindowsTotal, kind: 'section' },
      { name: 'Trim, fascia, soffit', value: trimTotal, kind: 'section' },
    ];
    if (state.options.opportunityCostEnabled) {
      breakdown.push({ name: 'Opportunity cost (your time)', value: opportunityCost, kind: 'modifier' });
    }
    if (state.options.equipmentWearEnabled) {
      breakdown.push({ name: 'Equipment wear', value: equipmentWear, kind: 'modifier' });
    }
    breakdown.push({ name: 'Sales tax', value: tax, kind: 'modifier' });
    breakdown.push({ name: 'Contingency', value: contingency, kind: 'modifier' });

    const chartData = [...breakdown]
      .filter((b) => b.value > 0)
      .sort((a, b) => b.value - a.value);

    return {
      buyTotalAll,
      buyPerSqft,
      timberHarvestTotal,
      thLabor,
      thFuel,
      thConsumables,
      sawmillTotal,
      smFuel,
      smBlades,
      smLabor,
      designTotal,
      dsLabor,
      dsPrint,
      sitePrepTotal,
      spRental,
      spSono,
      spBases,
      spConc,
      spGravel,
      spFuel,
      frameCuttingTotal,
      totalPieces,
      raisingTotal,
      rsCrew,
      rsSki,
      rsTrac,
      roofTotal,
      roofLabor,
      hardwareTotal,
      doorsWindowsTotal,
      trimTotal,
      subtotal,
      materialsBase,
      tax,
      opportunityCost,
      myHoursTotal,
      myHoursFromHarvest,
      myHoursFromRaising,
      myHoursFromMill,
      myHoursFromDesign,
      myHoursFromFrame,
      myHoursFromRoof,
      myHarvestLabor,
      myRaisingLabor,
      mySawmillLabor,
      myDesignLabor,
      myFrameLabor,
      myRoofLabor,
      selfLaborTotal,
      sawmillWear,
      skidsteerWear,
      tractorWear,
      equipmentWear,
      contingency,
      buildTotal,
      buildPerSqft,
      dollarDiff,
      pctDiffOnTotal,
      sqftDollarDiff,
      pctDiffOnSqft,
      buildTotalUnpaid,
      buildPerSqftUnpaid,
      dollarDiffUnpaid,
      pctDiffOnTotalUnpaid,
      pctDiffOnSqftUnpaid,
      breakdown,
      chartData,
    };
  }, [state]);

  const buildPaidIsCheaper = calc.dollarDiff < 0;
  const buildUnpaidIsCheaper = calc.dollarDiffUnpaid < 0;

  // Color cycle for the chart bars (CSS var-driven would be cleaner, but recharts needs concrete colors).
  const barColors = [
    '#2f7a3a',
    '#3b82c4',
    '#c47b3b',
    '#9b59b6',
    '#e2a72f',
    '#16a085',
    '#d35400',
    '#6c5ce7',
    '#e74c3c',
    '#1abc9c',
    '#7f8c8d',
    '#e67e22',
    '#34495e',
    '#27ae60',
  ];

  const renderWorkerRows = (
    workers: Worker[],
    onChange: (next: Worker[]) => void,
    addLabel: string
  ) => (
    <div className="scc-worker-list">
      <div className="scc-worker-head">
        <span>Person</span>
        <span>Hrs/day</span>
        <span>$ / hr</span>
        <span>Me?</span>
        <span></span>
      </div>
      {workers.map((w) => (
        <div className="scc-worker-row" key={w.id}>
          <input
            type="text"
            value={w.label}
            onChange={(e) => onChange(workers.map((x) => (x.id === w.id ? { ...x, label: e.target.value } : x)))}
            placeholder="Name"
          />
          <NumField
            value={w.hoursPerDay}
            onChange={(v) => onChange(workers.map((x) => (x.id === w.id ? { ...x, hoursPerDay: v } : x)))}
            compact
          />
          <NumField
            prefix="$"
            value={w.ratePerHour}
            onChange={(v) => onChange(workers.map((x) => (x.id === w.id ? { ...x, ratePerHour: v } : x)))}
            compact
          />
          <label className="scc-checkbox">
            <input
              type="checkbox"
              checked={w.isMe}
              onChange={(e) =>
                onChange(workers.map((x) => (x.id === w.id ? { ...x, isMe: e.target.checked } : x)))
              }
            />
          </label>
          <button
            type="button"
            className="scc-icon-btn scc-icon-btn--danger"
            onClick={() => onChange(workers.filter((x) => x.id !== w.id))}
            aria-label={`Remove ${w.label || 'worker'}`}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="scc-add-btn"
        onClick={() =>
          onChange([
            ...workers,
            { id: newId(), label: '', hoursPerDay: 8, ratePerHour: 25, isMe: false },
          ])
        }
      >
        <Plus size={14} />
        {addLabel}
      </button>
    </div>
  );

  return (
    <div className="scc-page">
      <header className="scc-header">
        <div className="scc-header-title">
          <Hammer size={22} />
          <h1>Equipment Shed: Build vs Buy</h1>
        </div>
        <div className="scc-header-actions">
          <button type="button" className="scc-btn scc-btn--ghost" onClick={handleReset}>
            <RotateCcw size={14} /> Reset
          </button>
          <button type="button" className="scc-btn scc-btn--ghost" onClick={handlePrint}>
            <Printer size={14} /> Print
          </button>
        </div>
      </header>

      <div className="scc-layout">
        <main className="scc-form">
          <Section
            icon={<ShoppingBag size={18} />}
            title="If you bought it"
            subtotal={calc.buyTotalAll}
            defaultOpen
          >
            <div className="scc-grid scc-grid--3">
              <NumField
                label="Footprint"
                value={state.buy.footprintSqft}
                onChange={(v) => update('buy', { footprintSqft: v })}
                suffix="sqft"
              />
              <NumField
                label="Purchase price"
                value={state.buy.purchasePrice}
                onChange={(v) => update('buy', { purchasePrice: v })}
                prefix="$"
              />
              <NumField
                label="Delivery / setup"
                value={state.buy.deliveryFee}
                onChange={(v) => update('buy', { deliveryFee: v })}
                prefix="$"
              />
            </div>
            <div className="scc-derived">
              Cost per sqft: <strong>{fmtMoneyCents(calc.buyPerSqft)}</strong>
            </div>
          </Section>

          <Section
            icon={<Ruler size={18} />}
            title="Your build's footprint"
            subtotal={0}
            defaultOpen
          >
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Footprint of the shed you'd build"
                value={state.build.footprintSqft}
                onChange={(v) => update('build', { footprintSqft: v })}
                suffix="sqft"
              />
            </div>
            <p className="scc-hint">
              The build footprint can differ from the prefab. Cost-per-sqft is what gets compared.
            </p>
          </Section>

          <Section
            icon={<TreePine size={18} />}
            title="Timber harvest"
            subtotal={calc.timberHarvestTotal}
          >
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Days to harvest"
                value={state.timberHarvest.days}
                onChange={(v) => update('timberHarvest', { days: v })}
                suffix="days"
              />
            </div>
            {renderWorkerRows(
              state.timberHarvest.workers,
              (next) => updateWorkerList('timberHarvest', 'workers', next),
              'Add person'
            )}
            <div className="scc-grid scc-grid--3 scc-grid--top-gap">
              <NumField
                label="Skidsteer fuel use"
                value={state.timberHarvest.skidsteerGalPerDay}
                onChange={(v) => update('timberHarvest', { skidsteerGalPerDay: v })}
                suffix="gal/day"
              />
              <NumField
                label="Days running skidsteer"
                value={state.timberHarvest.skidsteerDays}
                onChange={(v) => update('timberHarvest', { skidsteerDays: v })}
                suffix="days"
              />
              <NumField
                label="Diesel price"
                value={state.timberHarvest.dieselPricePerGal}
                onChange={(v) => update('timberHarvest', { dieselPricePerGal: v })}
                prefix="$"
                suffix="/gal"
                step={0.01}
              />
            </div>
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Chainsaw bar oil"
                value={state.timberHarvest.chainsawBarOil}
                onChange={(v) => update('timberHarvest', { chainsawBarOil: v })}
                prefix="$"
              />
              <NumField
                label="Chainsaw gas"
                value={state.timberHarvest.chainsawGas}
                onChange={(v) => update('timberHarvest', { chainsawGas: v })}
                prefix="$"
              />
            </div>
            <p className="scc-hint">
              Labor: {fmtMoney(calc.thLabor)} · Fuel: {fmtMoney(calc.thFuel)} · Consumables:{' '}
              {fmtMoney(calc.thConsumables)}
            </p>
          </Section>

          <Section
            icon={<Cog size={18} />}
            title="Sawmill"
            subtotal={calc.sawmillTotal}
          >
            <div className="scc-grid scc-grid--3">
              <NumField
                label="Hours of milling"
                value={state.sawmill.hours}
                onChange={(v) => update('sawmill', { hours: v })}
                suffix="hrs"
              />
              <NumField
                label="Sawmill fuel use"
                value={state.sawmill.galPerHour}
                onChange={(v) => update('sawmill', { galPerHour: v })}
                suffix="gal/hr"
                step={0.05}
              />
              <NumField
                label="Diesel price"
                value={state.sawmill.dieselPricePerGal}
                onChange={(v) => update('sawmill', { dieselPricePerGal: v })}
                prefix="$"
                suffix="/gal"
                step={0.01}
              />
            </div>
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Blade replacements"
                value={state.sawmill.bladeReplacements}
                onChange={(v) => update('sawmill', { bladeReplacements: v })}
              />
              <NumField
                label="Blade price"
                value={state.sawmill.bladePrice}
                onChange={(v) => update('sawmill', { bladePrice: v })}
                prefix="$"
              />
            </div>
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Mill operator hours"
                value={state.sawmill.operatorHours}
                onChange={(v) => update('sawmill', { operatorHours: v })}
                suffix="hrs"
              />
              <NumField
                label="Operator rate"
                value={state.sawmill.operatorRate}
                onChange={(v) => update('sawmill', { operatorRate: v })}
                prefix="$"
                suffix="/hr"
              />
            </div>
            <label className="scc-me-toggle">
              <input
                type="checkbox"
                checked={state.sawmill.operatorIsMe}
                onChange={(e) => update('sawmill', { operatorIsMe: e.target.checked })}
              />
              <span>I'm the mill operator</span>
              {state.sawmill.operatorIsMe && (
                <span className="scc-me-badge">{fmtMoney(calc.mySawmillLabor)}</span>
              )}
            </label>
            <p className="scc-hint">
              Fuel: {fmtMoney(calc.smFuel)} · Blades: {fmtMoney(calc.smBlades)} · Labor:{' '}
              {fmtMoney(calc.smLabor)}
            </p>
          </Section>

          <Section
            icon={<PencilRuler size={18} />}
            title="Design / drafting"
            subtotal={calc.designTotal}
          >
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Onshape hours"
                value={state.design.onshapeHours}
                onChange={(v) => update('design', { onshapeHours: v })}
                suffix="hrs"
              />
              <NumField
                label="Hourly rate"
                value={state.design.onshapeRate}
                onChange={(v) => update('design', { onshapeRate: v })}
                prefix="$"
                suffix="/hr"
              />
            </div>
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Pages of printing"
                value={state.design.pages}
                onChange={(v) => update('design', { pages: v })}
                suffix="pages"
              />
              <NumField
                label="Cost per page"
                value={state.design.pricePerPage}
                onChange={(v) => update('design', { pricePerPage: v })}
                prefix="$"
                step={0.01}
              />
            </div>
            <label className="scc-me-toggle">
              <input
                type="checkbox"
                checked={state.design.onshapeIsMe}
                onChange={(e) => update('design', { onshapeIsMe: e.target.checked })}
              />
              <span>I'm doing the drafting</span>
              {state.design.onshapeIsMe && (
                <span className="scc-me-badge">{fmtMoney(calc.myDesignLabor)}</span>
              )}
            </label>
            <p className="scc-hint">
              Drafting: {fmtMoney(calc.dsLabor)} · Printing: {fmtMoney(calc.dsPrint)}
            </p>
          </Section>

          <Section
            icon={<Shovel size={18} />}
            title="Site prep"
            subtotal={calc.sitePrepTotal}
          >
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Auger bit rental days"
                value={state.sitePrep.augerRentalDays}
                onChange={(v) => update('sitePrep', { augerRentalDays: v })}
                suffix="days"
              />
              <NumField
                label="Daily rental rate"
                value={state.sitePrep.augerDailyRate}
                onChange={(v) => update('sitePrep', { augerDailyRate: v })}
                prefix="$"
                suffix="/day"
              />
            </div>
            <div className="scc-grid scc-grid--3">
              <NumField
                label="Number of post holes"
                value={state.sitePrep.holes}
                onChange={(v) => update('sitePrep', { holes: v })}
              />
              <NumField
                label="Sonotubes / forms"
                value={state.sitePrep.sonotubes}
                onChange={(v) => update('sitePrep', { sonotubes: v })}
              />
              <NumField
                label="Sonotube price each"
                value={state.sitePrep.sonotubePrice}
                onChange={(v) => update('sitePrep', { sonotubePrice: v })}
                prefix="$"
              />
            </div>
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Post bases / anchors"
                value={state.sitePrep.postBases}
                onChange={(v) => update('sitePrep', { postBases: v })}
              />
              <NumField
                label="Post base price each"
                value={state.sitePrep.postBasePrice}
                onChange={(v) => update('sitePrep', { postBasePrice: v })}
                prefix="$"
              />
            </div>
            <div className="scc-grid scc-grid--3">
              <NumField
                label="Concrete"
                value={state.sitePrep.concreteYards}
                onChange={(v) => update('sitePrep', { concreteYards: v })}
                suffix="yd³"
              />
              <NumField
                label="Concrete $ / yd³"
                value={state.sitePrep.concretePricePerYard}
                onChange={(v) => update('sitePrep', { concretePricePerYard: v })}
                prefix="$"
              />
              <NumField
                label="Concrete delivery"
                value={state.sitePrep.concreteDelivery}
                onChange={(v) => update('sitePrep', { concreteDelivery: v })}
                prefix="$"
              />
            </div>
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Gravel/chert truckloads"
                value={state.sitePrep.gravelTrucks}
                onChange={(v) => update('sitePrep', { gravelTrucks: v })}
              />
              <NumField
                label="Price per truckload"
                value={state.sitePrep.gravelPricePerTruck}
                onChange={(v) => update('sitePrep', { gravelPricePerTruck: v })}
                prefix="$"
              />
            </div>
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Skidsteer diesel for site prep"
                value={state.sitePrep.skidsteerGal}
                onChange={(v) => update('sitePrep', { skidsteerGal: v })}
                suffix="gal"
              />
              <NumField
                label="Diesel price"
                value={state.sitePrep.dieselPricePerGal}
                onChange={(v) => update('sitePrep', { dieselPricePerGal: v })}
                prefix="$"
                suffix="/gal"
                step={0.01}
              />
            </div>
            <p className="scc-hint">
              Rental: {fmtMoney(calc.spRental)} · Sonotubes: {fmtMoney(calc.spSono)} · Bases:{' '}
              {fmtMoney(calc.spBases)} · Concrete: {fmtMoney(calc.spConc)} · Gravel:{' '}
              {fmtMoney(calc.spGravel)} · Fuel: {fmtMoney(calc.spFuel)}
            </p>
          </Section>

          <Section
            icon={<Scissors size={18} />}
            title="Frame cutting"
            subtotal={calc.frameCuttingTotal}
          >
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Number of guys"
                value={state.frameCutting.guys}
                onChange={(v) => update('frameCutting', { guys: v })}
              />
              <NumField
                label="Days expected"
                value={state.frameCutting.days}
                onChange={(v) => update('frameCutting', { days: v })}
                suffix="days"
              />
            </div>
            <div className="scc-grid scc-grid--3">
              <NumField
                label="Hours per day"
                value={state.frameCutting.hoursPerDay}
                onChange={(v) => update('frameCutting', { hoursPerDay: v })}
                suffix="hrs"
              />
              <NumField
                label="Rate per hour"
                value={state.frameCutting.ratePerHour}
                onChange={(v) => update('frameCutting', { ratePerHour: v })}
                prefix="$"
                suffix="/hr"
              />
              <NumField
                label="Pieces per day (sanity check)"
                value={state.frameCutting.piecesPerDay}
                onChange={(v) => update('frameCutting', { piecesPerDay: v })}
              />
            </div>
            <label className="scc-me-toggle">
              <input
                type="checkbox"
                checked={state.frameCutting.includeMe}
                onChange={(e) => update('frameCutting', { includeMe: e.target.checked })}
              />
              <span>I'm one of the guys cutting</span>
              {state.frameCutting.includeMe && (
                <span className="scc-me-badge">{fmtMoney(calc.myFrameLabor)}</span>
              )}
            </label>
            <p className="scc-hint">
              Total pieces cut: <strong>{fmtNum(calc.totalPieces)}</strong> · Labor:{' '}
              {fmtMoney(calc.frameCuttingTotal)}
            </p>
          </Section>

          <Section
            icon={<Frame size={18} />}
            title="Raising"
            subtotal={calc.raisingTotal}
          >
            <div className="scc-grid scc-grid--3">
              <NumField
                label="Days"
                value={state.raising.days}
                onChange={(v) => update('raising', { days: v })}
                suffix="days"
              />
              <NumField
                label="Skidsteer fuel"
                value={state.raising.skidsteerGalPerDay}
                onChange={(v) => update('raising', { skidsteerGalPerDay: v })}
                suffix="gal/day"
              />
              <NumField
                label="Tractor fuel"
                value={state.raising.tractorGalPerDay}
                onChange={(v) => update('raising', { tractorGalPerDay: v })}
                suffix="gal/day"
              />
            </div>
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Diesel price"
                value={state.raising.dieselPricePerGal}
                onChange={(v) => update('raising', { dieselPricePerGal: v })}
                prefix="$"
                suffix="/gal"
                step={0.01}
              />
            </div>
            {renderWorkerRows(
              state.raising.crew,
              (next) => updateWorkerList('raising', 'crew', next),
              'Add crew member'
            )}
            <p className="scc-hint">
              Crew: {fmtMoney(calc.rsCrew)} · Skidsteer fuel: {fmtMoney(calc.rsSki)} · Tractor fuel:{' '}
              {fmtMoney(calc.rsTrac)}
            </p>
          </Section>

          <Section
            icon={<Home size={18} />}
            title="Roof"
            subtotal={calc.roofTotal}
          >
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Roofing material"
                value={state.roof.materialCost}
                onChange={(v) => update('roof', { materialCost: v })}
                prefix="$"
              />
              <NumField
                label="Underlayment, drip edge, ridge, fasteners"
                value={state.roof.accessoriesCost}
                onChange={(v) => update('roof', { accessoriesCost: v })}
                prefix="$"
              />
            </div>
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Roof labor hours"
                value={state.roof.laborHours}
                onChange={(v) => update('roof', { laborHours: v })}
                suffix="hrs"
              />
              <NumField
                label="Roof labor rate"
                value={state.roof.laborRate}
                onChange={(v) => update('roof', { laborRate: v })}
                prefix="$"
                suffix="/hr"
              />
            </div>
            <label className="scc-me-toggle">
              <input
                type="checkbox"
                checked={state.roof.laborIsMe}
                onChange={(e) => update('roof', { laborIsMe: e.target.checked })}
              />
              <span>I'm doing the roof labor</span>
              {state.roof.laborIsMe && (
                <span className="scc-me-badge">{fmtMoney(calc.myRoofLabor)}</span>
              )}
            </label>
            <p className="scc-hint">Labor: {fmtMoney(calc.roofLabor)}</p>
          </Section>

          <Section
            icon={<Wrench size={18} />}
            title="Hardware & finish"
            subtotal={calc.hardwareTotal}
          >
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Lag bolts, screws, nails"
                value={state.hardware.fastenersCost}
                onChange={(v) => update('hardware', { fastenersCost: v })}
                prefix="$"
              />
              <NumField
                label="Sealant / stain / finish"
                value={state.hardware.finishCost}
                onChange={(v) => update('hardware', { finishCost: v })}
                prefix="$"
              />
            </div>
          </Section>

          <Section
            icon={<DoorOpen size={18} />}
            title="Doors & windows"
            subtotal={calc.doorsWindowsTotal}
          >
            <div className="scc-dw-list">
              <div className="scc-dw-head">
                <span>Item</span>
                <span>Qty</span>
                <span>$ each</span>
                <span></span>
              </div>
              {state.doorsWindows.map((d) => (
                <div className="scc-dw-row" key={d.id}>
                  <input
                    type="text"
                    value={d.label}
                    onChange={(e) =>
                      updateDoorsWindows(
                        state.doorsWindows.map((x) => (x.id === d.id ? { ...x, label: e.target.value } : x))
                      )
                    }
                    placeholder="e.g. Sliding door"
                  />
                  <NumField
                    value={d.qty}
                    onChange={(v) =>
                      updateDoorsWindows(
                        state.doorsWindows.map((x) => (x.id === d.id ? { ...x, qty: v } : x))
                      )
                    }
                    compact
                  />
                  <NumField
                    prefix="$"
                    value={d.costEach}
                    onChange={(v) =>
                      updateDoorsWindows(
                        state.doorsWindows.map((x) => (x.id === d.id ? { ...x, costEach: v } : x))
                      )
                    }
                    compact
                  />
                  <button
                    type="button"
                    className="scc-icon-btn scc-icon-btn--danger"
                    onClick={() =>
                      updateDoorsWindows(state.doorsWindows.filter((x) => x.id !== d.id))
                    }
                    aria-label={`Remove ${d.label || 'item'}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="scc-add-btn"
                onClick={() =>
                  updateDoorsWindows([
                    ...state.doorsWindows,
                    { id: newId(), label: '', qty: 1, costEach: 0 },
                  ])
                }
              >
                <Plus size={14} /> Add door / window
              </button>
            </div>
          </Section>

          <Section
            icon={<Sparkles size={18} />}
            title="Trim, fascia, soffit"
            subtotal={calc.trimTotal}
          >
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Lump sum"
                value={state.trim.lumpSum}
                onChange={(v) => update('trim', { lumpSum: v })}
                prefix="$"
              />
            </div>
          </Section>

          <Section
            icon={<Receipt size={18} />}
            title="Tax & contingency"
            subtotal={calc.tax + calc.contingency}
          >
            <div className="scc-grid scc-grid--2">
              <NumField
                label="Sales tax (on materials only)"
                value={state.taxContingency.salesTaxPct}
                onChange={(v) => update('taxContingency', { salesTaxPct: v })}
                suffix="%"
                step={0.25}
              />
              <NumField
                label="Contingency buffer"
                value={state.taxContingency.contingencyPct}
                onChange={(v) => update('taxContingency', { contingencyPct: v })}
                suffix="%"
                step={1}
              />
            </div>
            <p className="scc-hint">
              Materials base: <strong>{fmtMoney(calc.materialsBase)}</strong> · Tax:{' '}
              <strong>{fmtMoney(calc.tax)}</strong> · Contingency:{' '}
              <strong>{fmtMoney(calc.contingency)}</strong>
            </p>
          </Section>

          <Section
            icon={<Cog size={18} />}
            title="Optional: opportunity cost & equipment wear"
            subtotal={calc.opportunityCost + calc.equipmentWear}
            defaultOpen={false}
          >
            <div className="scc-toggle-block">
              <label className="scc-toggle">
                <input
                  type="checkbox"
                  checked={state.options.opportunityCostEnabled}
                  onChange={(e) =>
                    update('options', { opportunityCostEnabled: e.target.checked })
                  }
                />
                <span>Include opportunity cost of my own time</span>
              </label>
              {state.options.opportunityCostEnabled && (
                <>
                  <div className="scc-grid scc-grid--2">
                    <NumField
                      label="My time is worth"
                      value={state.options.opportunityCostRate}
                      onChange={(v) => update('options', { opportunityCostRate: v })}
                      prefix="$"
                      suffix="/hr"
                    />
                    <NumField
                      label="Extra personal hours not captured above"
                      value={state.options.extraPersonalHours}
                      onChange={(v) => update('options', { extraPersonalHours: v })}
                      suffix="hrs"
                    />
                  </div>
                  <p className="scc-hint">
                    My hours: harvest {fmtNum(calc.myHoursFromHarvest)} + raising{' '}
                    {fmtNum(calc.myHoursFromRaising)} + mill {fmtNum(calc.myHoursFromMill)} +
                    drafting {fmtNum(calc.myHoursFromDesign)} + frame{' '}
                    {fmtNum(calc.myHoursFromFrame)} + roof {fmtNum(calc.myHoursFromRoof)} + extra{' '}
                    {fmtNum(state.options.extraPersonalHours)} ={' '}
                    <strong>{fmtNum(calc.myHoursTotal)} hrs</strong> ·{' '}
                    {fmtMoney(calc.opportunityCost)}
                  </p>
                  <p className="scc-hint">
                    Only counts sections where the "Me?" / "I'm…" box is checked.
                  </p>
                </>
              )}
            </div>

            <div className="scc-toggle-block">
              <label className="scc-toggle">
                <input
                  type="checkbox"
                  checked={state.options.equipmentWearEnabled}
                  onChange={(e) =>
                    update('options', { equipmentWearEnabled: e.target.checked })
                  }
                />
                <span>Include equipment wear / depreciation</span>
              </label>
              {state.options.equipmentWearEnabled && (
                <>
                  <div className="scc-grid scc-grid--2">
                    <NumField
                      label="Sawmill total run hours"
                      value={state.options.sawmillWearHours}
                      onChange={(v) => update('options', { sawmillWearHours: v })}
                      suffix="hrs"
                    />
                    <NumField
                      label="Sawmill wear rate"
                      value={state.options.sawmillWearRate}
                      onChange={(v) => update('options', { sawmillWearRate: v })}
                      prefix="$"
                      suffix="/hr"
                    />
                  </div>
                  <div className="scc-grid scc-grid--2">
                    <NumField
                      label="Skidsteer total run hours"
                      value={state.options.skidsteerWearHours}
                      onChange={(v) => update('options', { skidsteerWearHours: v })}
                      suffix="hrs"
                    />
                    <NumField
                      label="Skidsteer wear rate"
                      value={state.options.skidsteerWearRate}
                      onChange={(v) => update('options', { skidsteerWearRate: v })}
                      prefix="$"
                      suffix="/hr"
                    />
                  </div>
                  <div className="scc-grid scc-grid--2">
                    <NumField
                      label="Tractor total run hours"
                      value={state.options.tractorWearHours}
                      onChange={(v) => update('options', { tractorWearHours: v })}
                      suffix="hrs"
                    />
                    <NumField
                      label="Tractor wear rate"
                      value={state.options.tractorWearRate}
                      onChange={(v) => update('options', { tractorWearRate: v })}
                      prefix="$"
                      suffix="/hr"
                    />
                  </div>
                  <p className="scc-hint">
                    Sawmill: {fmtMoney(calc.sawmillWear)} · Skidsteer:{' '}
                    {fmtMoney(calc.skidsteerWear)} · Tractor: {fmtMoney(calc.tractorWear)}
                  </p>
                </>
              )}
            </div>
          </Section>
        </main>

        <aside className="scc-results">
          <div className="scc-results-inner">
            <div className="scc-results-header">
              <h2>Comparison</h2>
            </div>

            <div className="scc-result-stack">
              <div className="scc-card scc-card--buy">
                <div className="scc-card-row">
                  <div className="scc-card-label">Buy</div>
                  <div className="scc-card-tag scc-card-tag--neutral">Reference</div>
                </div>
                <div className="scc-card-money">
                  {fmtMoneyCents(calc.buyPerSqft)}
                  <span>/sqft</span>
                </div>
                <div className="scc-card-sub">
                  {fmtMoney(calc.buyTotalAll)} total · {fmtNum(state.buy.footprintSqft)} sqft
                </div>
              </div>

              <div
                className={`scc-card scc-card--build${
                  buildPaidIsCheaper ? ' scc-card--good' : ' scc-card--bad'
                }`}
              >
                <div className="scc-card-row">
                  <div className="scc-card-label">Build · paying myself</div>
                  <div
                    className={`scc-card-tag ${
                      buildPaidIsCheaper ? 'scc-card-tag--good' : 'scc-card-tag--bad'
                    }`}
                  >
                    {buildPaidIsCheaper ? <TrendingDown size={12} /> : <TrendingUp size={12} />}
                    {fmtPct(calc.pctDiffOnSqft)}
                  </div>
                </div>
                <div className="scc-card-money">
                  {fmtMoneyCents(calc.buildPerSqft)}
                  <span>/sqft</span>
                </div>
                <div className="scc-card-sub">
                  {fmtMoney(calc.buildTotal)} total ·{' '}
                  {buildPaidIsCheaper ? 'saves' : 'costs'} {fmtMoney(Math.abs(calc.dollarDiff))} vs
                  buying ({fmtPct(calc.pctDiffOnTotal)})
                </div>
              </div>

              <div
                className={`scc-card scc-card--build${
                  buildUnpaidIsCheaper ? ' scc-card--good' : ' scc-card--bad'
                }`}
              >
                <div className="scc-card-row">
                  <div className="scc-card-label">Build · NOT paying myself</div>
                  <div
                    className={`scc-card-tag ${
                      buildUnpaidIsCheaper ? 'scc-card-tag--good' : 'scc-card-tag--bad'
                    }`}
                  >
                    {buildUnpaidIsCheaper ? <TrendingDown size={12} /> : <TrendingUp size={12} />}
                    {fmtPct(calc.pctDiffOnSqftUnpaid)}
                  </div>
                </div>
                <div className="scc-card-money">
                  {fmtMoneyCents(calc.buildPerSqftUnpaid)}
                  <span>/sqft</span>
                </div>
                <div className="scc-card-sub">
                  {fmtMoney(calc.buildTotalUnpaid)} total ·{' '}
                  {buildUnpaidIsCheaper ? 'saves' : 'costs'}{' '}
                  {fmtMoney(Math.abs(calc.dollarDiffUnpaid))} vs buying (
                  {fmtPct(calc.pctDiffOnTotalUnpaid)})
                </div>
                <div className="scc-card-meta">
                  Skips {fmtMoney(calc.selfLaborTotal)} of your own labor (sections where "Me?" is
                  checked)
                </div>
              </div>
            </div>

            <div className="scc-results-table">
              <div className="scc-results-table-row scc-results-table-row--head">
                <span>Category</span>
                <span>Cost</span>
                <span>%</span>
              </div>
              {calc.breakdown.map((b) => {
                const pct = calc.buildTotal > 0 ? (b.value / calc.buildTotal) * 100 : 0;
                return (
                  <div
                    key={b.name}
                    className={`scc-results-table-row scc-results-table-row--${b.kind}`}
                  >
                    <span>{b.name}</span>
                    <span>{fmtMoney(b.value)}</span>
                    <span>{fmtNum(pct, 1)}%</span>
                  </div>
                );
              })}
              <div className="scc-results-table-row scc-results-table-row--total">
                <span>Build total (paying myself)</span>
                <span>{fmtMoney(calc.buildTotal)}</span>
                <span>100%</span>
              </div>
              {calc.selfLaborTotal > 0 && (
                <>
                  <div className="scc-results-table-row scc-results-table-row--modifier">
                    <span>– Your own labor</span>
                    <span>−{fmtMoney(calc.selfLaborTotal)}</span>
                    <span>
                      {fmtNum(
                        calc.buildTotal > 0 ? (calc.selfLaborTotal / calc.buildTotal) * 100 : 0,
                        1
                      )}
                      %
                    </span>
                  </div>
                  <div className="scc-results-table-row scc-results-table-row--total">
                    <span>Build total (NOT paying myself)</span>
                    <span>{fmtMoney(calc.buildTotalUnpaid)}</span>
                    <span></span>
                  </div>
                </>
              )}
            </div>

            <div className="scc-chart">
              <ResponsiveContainer width="100%" height={Math.max(220, calc.chartData.length * 28)}>
                <BarChart
                  data={calc.chartData}
                  layout="vertical"
                  margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                  <XAxis
                    type="number"
                    tickFormatter={(v) => '$' + Math.round(v / 1000) + 'k'}
                    fontSize={11}
                    stroke="#6b7280"
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={150}
                    fontSize={11}
                    stroke="#374151"
                  />
                  <Tooltip
                    formatter={(value) => fmtMoney(typeof value === 'number' ? value : Number(value) || 0)}
                    cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {calc.chartData.map((_, i) => (
                      <Cell key={i} fill={barColors[i % barColors.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </aside>
      </div>

      <footer className="scc-footer">
        Inputs auto-save to your browser only. Nothing leaves this device.
      </footer>
    </div>
  );
};

export default ShedCostCalculator;
